import React from "react";
import {
  Grid,
  Typography,
  TextField,
  Card,
  IconButton,
} from "@material-ui/core";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { ExpandMore } from "@material-ui/icons";
import CloseIcon from "@material-ui/icons/Close";

/**
 * A MUI Accordion that can be used as a section in the EditSupplier component.
 * It handles its own expanded state and notifies its parent when it is expanded
 * or collapsed. The parent should provide a function to set the activeId state
 * variable to the id of the currently expanded section.
 *
 * @param {object} props - component props
 * @param {string} props.heading - The heading of the section.
 * @param {React.ReactNode} props.children - The content of the section.
 * @param {string} props.id - The id of the section.
 * @param {string} props.activeId - The id of the currently expanded section.
 * @param {Function} props.handleAccordianExpanded - A function to set the activeId
 * state variable to the id of the currently expanded section.
 */
export function SectionAccordian({
  heading,
  children,
  id,
  activeId,
  handleAccordianExpanded,
}) {
  return (
    <Grid item xs={12}>
      <Accordion
        expanded={id === activeId}
        onChange={() => {
          handleAccordianExpanded(id);
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>{heading}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container xs={12} spacing={3}>
            {children}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
}

/**
 * A React component that renders a block of contact information for a given
 * contact type. It takes the contact type, an array of contacts, and a function
 * to handle changes to the contact information.
 *
 * @param {object} props - component props
 * @param {object} props.informationType - The contact type.
 * @param {Array} props.contacts - The array of contacts.
 * @param {Function} props.handleContact - The function to handle changes to the contact information.
 */
export function ContactInformationBlock({
  informationType,
  contacts,
  handleContact,
}) {
  return (
    <>
      <Grid item xs={12}>
        <Typography>{`${informationType?.contact_type_name} Contact`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={`${informationType?.contact_type_name} Contact First Name`}
          variant="outlined"
          name="contact_first_name"
          required={informationType.is_required}
          value={
            contacts.find(item => item.contact_type_id === informationType.id)
              ?.contact_first_name || ""
          }
          onChange={e => {
            handleContact(
              informationType.id,
              "contact_first_name",
              e.target.value
            );
          }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={`${informationType?.contact_type_name} Contact Surname`}
          variant="outlined"
          name="contact_surname"
          required={informationType.is_required}
          value={
            contacts.find(item => item.contact_type_id === informationType.id)
              ?.contact_surname || ""
          }
          onChange={e => {
            handleContact(
              informationType.id,
              "contact_surname",
              e.target.value
            );
          }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={`${informationType?.contact_type_name} Contact Number`}
          variant="outlined"
          name="contact_number"
          required={informationType.is_required}
          value={
            contacts.find(item => item.contact_type_id === informationType.id)
              ?.contact_number || ""
          }
          onChange={e => {
            handleContact(informationType.id, "contact_number", e.target.value);
          }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={`${informationType?.contact_type_name} Contact Email`}
          variant="outlined"
          name="contact_email"
          required={informationType.is_required}
          value={
            contacts.find(item => item.contact_type_id === informationType.id)
              ?.contact_email || ""
          }
          onChange={e => {
            handleContact(informationType.id, "contact_email", e.target.value);
          }}
          fullWidth
        />
      </Grid>
    </>
  );
}

/**
 * AssistEmailCard renders a card for an admin email with a delete button.
 *
 * @param {{ adminUser: { value: string, label: string }, removeAssistAdmin: Function, type: string }} props - component props
 * @param {object} props.adminUser - The admin user object containing the email and name.
 * @param {Function} props.removeAssistAdmin - The function to remove the admin from the list.
 * @param {string} props.type - The type of assist email.
 *
 * @returns {React.Element} The rendered AssistEmailCard component.
 */
export function AssistEmailCard({ adminUser, removeAssistAdmin, type }) {
  const { value, label } = adminUser;
  return (
    <Card
      key={value}
      style={{
        margin: 5,
      }}
    >
      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid item>
          <Typography
            style={{ marginLeft: 15, marginRight: 10 }}
            variant="body2"
          >
            {label}
          </Typography>
        </Grid>
        <Grid item>
          <IconButton
            aria-label="delete"
            size="small"
            style={{
              borderRadius: 0, // Default border radius
              color: "red",
              transition: "background-color 0.3s ease, border-radius 0.3s ease",
              "&:hover": {
                borderRadius: "0 16px 16px 0", // Custom border radius on hover
              },
              "&:active": {
                borderRadius: "0 16px 16px 0", // Ensure it matches hover
              },
            }}
            onClick={() => {
              removeAssistAdmin(value, type);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Card>
  );
}
